import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  AfterViewInit,
  Output,
  ViewChild,
  ChangeDetectorRef
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  AdaCustomization,
  AdaCustomizationsService,
  DropdownQuestion,
  DynamicFormComponent,
  RadiobuttonQuestion,
  PopUpService
} from 'core/index';
import { CustomizationsQuestionsService } from '../customizations-questions.service';
import { CustomizationVisualizerPopupComponent } from '../customization-visualizer-popup/customization-visualizer-popup.component';
import { DocumentsService, Document } from 'app/documents';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';

const LIMIT_PACKAGES = 25;

@Component({
  selector: 'lms-customization-editor',
  templateUrl: './customization-editor.component.html',
  styleUrls: ['./customization-editor.component.scss']
})
export class CustomizationEditorComponent implements OnInit, AfterViewInit {
  @Input() customization: AdaCustomization;
  @Input() defaults: any;
  @Output() saved = new EventEmitter<AdaCustomization>();
  @ViewChild(DynamicFormComponent, { static: true }) public dynamicForm: DynamicFormComponent;
  @ViewChild(CustomizationVisualizerPopupComponent, { static: false })
  public visualizerPopup: CustomizationVisualizerPopupComponent;
  @ViewChild('documentSelectedList', { static: false }) public documentSelectedList: any[];

  public questions: any[];

  public allDocuments: Array<Document>;
  public filteredDocuments: Array<any>;
  public documentsInCustomization: Array<Document>;
  public containers = [];
  public turnContainers = [];
  public elements = [];

  public clientId;
  public client;
  public defaultFilterSelect = 'all';
  public defaultRadioBtn = 'docs';
  public openPopupFlag = false;
  public dirtyFlag = false;

  public questionSelect: DropdownQuestion;
  public questionRadioBtn: RadiobuttonQuestion;
  public filterOptions = [
    { key: 'all', value: this.translate.instant('customizations.filterDocs.all') },
    { key: 'notUsed', value: this.translate.instant('customizations.filterDocs.notUsed') },
    { key: 'onlyVideos', value: this.translate.instant('customizations.filterDocs.onlyVideos') },
    { key: 'onlyAudios', value: this.translate.instant('customizations.filterDocs.onlyAudios') },
    { key: 'onlyPdfs', value: this.translate.instant('customizations.filterDocs.onlyPdfs') },
    { key: 'onlyImages', value: this.translate.instant('customizations.filterDocs.onlyImages') }
  ];

  constructor(
    public route: ActivatedRoute,
    public adaCustomizationsService: AdaCustomizationsService,
    public documentsService: DocumentsService,
    public popupService: PopUpService,
    public customizationsQuestions: CustomizationsQuestionsService,
    private translate: TranslateService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.questions = this.customizationsQuestions.getQuestions(this.customization);
    const clientQuestion = this.questions.find((question) => question.key === 'clientId');
    this.clientId = clientQuestion.value;
    this.loadDocuments();
    this.setQuestions();
  }

  ngAfterViewInit() {
    this.dynamicForm.form.valueChanges.subscribe((data) => {
      if (this.clientId !== data.clientId) {
        this.clientId = data.clientId;
        this.defaultFilterSelect = 'all';
        this.loadDocuments();
      }
    });
    this.cd.detectChanges();
    this.openPopupFlag = false;
    this.dirtyFlag = false;
  }

  initTurnContainers() {
    // Create a container for each possible turn
    const arr = new Array(LIMIT_PACKAGES).fill({});
    const turnContainers = arr.map((elem, i) => {
      return { turn: i + 2, documents: [] };
    });
    return turnContainers;
  }

  public turnDocumentList(index) {
    return index
      ? this.turnContainers[index]['documents'].length === 0
        ? []
        : this.turnContainers[index]['documents']
      : [];
  }

  loadDocuments() {
    this.turnContainers = this.initTurnContainers();
    this.documentSelectedList = this.initTurnContainers();

    if (!this.clientId) {
      this.allDocuments = [];
      this.filteredDocuments = [];
      return;
    }

    this.documentsService
      .fetch({
        clientId: this.clientId,
        aggregates: ['numQuestions', 'numCustomizations']
      })
      .then((documents) => {
        this.allDocuments = documents.docs.filter((doc) => doc);
        this.filterDocuments();
        this.filteredDocuments.map((doc) => (doc.documentId = doc.id));

        if (this.customization) {
          this.customization.content.map((item) => {
            const tmp = this.turnContainers.find((container) => container.turn === item.turn);
            if (tmp) {
              item.documents.map((doc) => {
                tmp.documents.push(doc);
                // Find the document that is assigned to the customization to add the "numQuestions" property in the "right" column
                const currentDoc = this.filteredDocuments.find((tmp2) => doc.documentId === tmp2.id);
                if (currentDoc) {
                  doc.numQuestions = currentDoc ? currentDoc.numQuestions : 0;
                  // Remove the document from the "left" column if it exists in the customization
                  this.filteredDocuments = this.filteredDocuments.filter((tmp2) => doc.documentId !== tmp2.id);
                }
              });
            } else {
              console.error(`no container for turn ${item.turn}`);
            }
          });
        }

        this.dynamicForm.form.markAsDirty();
      });
  }

  /**
   * the Creation of the questions for customization editor
   * @private
   */
  private setQuestions(): void {
    const customAction = this.customization ? 'customization.edit' : 'customization.create';
    this.questionSelect = new DropdownQuestion({
      key: 'filterFiles',
      action: customAction,
      label: 'customizations.documents.filter',
      placeholder: 'customizations.documents.filter',
      options: this.filterOptions,
      separator: true,
      value: this.defaultFilterSelect,
      eventHandlers: {
        onChange: (value) => {
          this.updateDocsFilter(value);
        }
      }
    });

    this.questionRadioBtn = new RadiobuttonQuestion({
      key: 'reorderFiles',
      action: customAction,
      options: [
        {
          label: this.translate.instant('customizations.dragType.docs'),
          value: 'docs'
        },
        {
          label: this.translate.instant('customizations.dragType.turns'),
          value: 'turns'
        }
      ],
      value: this.defaultRadioBtn
    });
  }

  private filterDocuments() {
    let filter;
    switch (this.defaultFilterSelect) {
      case 'all':
        filter = (doc) => doc.numQuestions >= 1;
        break;
      case 'notUsed':
        filter = (doc) => doc.numQuestions >= 1 && doc.numCustomizations === 0;
        break;
      case 'onlyVideos':
        filter = (doc) => doc.numQuestions >= 1 && doc.type === 'embedded_video';
        break;
      case 'onlyAudios':
        filter = (doc) => doc.numQuestions >= 1 && doc.type === 'audio';
        break;
      case 'onlyPdfs':
        filter = (doc) => doc.numQuestions >= 1 && doc.type === 'pdf';
        break;
      case 'onlyImages':
        filter = (doc) => doc.numQuestions >= 1 && doc.type === 'image';
        break;
    }

    this.filteredDocuments = this.allDocuments.filter(filter);
  }

  undo() {
    this.loadDocuments();
  }

  updateDocsFilter(event) {
    this.defaultFilterSelect = event;
    this.loadDocuments();
  }

  turnContainerMoved(index, turn) {
    // Recalculate the order when the user has moved a package
    this.turnContainers.map((elem, i) => {
      elem.turn = i + 2;
    });
  }

  automaticDistribution() {
    // Take all the docs already assigned to the current customization and move them to the beginning of
    // the list of documents before applying the automatic distribution
    const existingDocs = [];
    this.turnContainers.map((turnContainer) => {
      turnContainer.documents.map((doc) => {
        existingDocs.push(doc);
      });
      turnContainer.documents = [];
    });
    existingDocs.reverse().map((doc) => {
      this.filteredDocuments.unshift(doc);
    });

    const step = LIMIT_PACKAGES / this.filteredDocuments.length;
    let nextContainer = 0;
    let tmp = 0;
    while (this.filteredDocuments.length) {
      const doc = this.filteredDocuments.splice(0, 1);
      this.turnContainers[nextContainer].documents.push(doc[0]);
      if (step <= 1) {
        nextContainer = (nextContainer + 1) % LIMIT_PACKAGES;
      } else {
        tmp += step;
        nextContainer = Math.round(tmp) % LIMIT_PACKAGES;
      }
    }
  }

  getCustomizationFromForm() {
    const controls = this.dynamicForm.form.controls;
    const customizationData = new AdaCustomization();
    customizationData.clientId = controls.clientId ? controls.clientId.value : this.clientId;
    customizationData.defaultForClient = controls.defaultForClient ? controls.defaultForClient.value : false;
    customizationData.defaultForLanguage = controls.defaultForLanguage ? controls.defaultForLanguage.value : '';
    customizationData.name = controls.name ? controls.name.value : '';
    customizationData.languages = controls.languages ? [controls.languages.value] : [];

    customizationData.content = this.turnContainers.filter((turnContainer) => turnContainer.documents.length);
    return customizationData;
  }

  submit() {
    const customizationData = this.getCustomizationFromForm();
    if (this.customization) {
      this.onUpdate(customizationData);
    } else {
      this.onCreate(customizationData);
    }
  }

  get submitDisabled() {
    const isClientSelected = this.dynamicForm.form.controls.clientId && this.dynamicForm.form.controls.clientId.value;
    const isNameSelected = this.dynamicForm.form.controls.name && this.dynamicForm.form.controls.name.value;
    const isAnyDocAssigned = this.turnContainers.some((turnContainer) => turnContainer.documents.length);
    const isLanguageSelected =
      this.dynamicForm.form.controls.languages && this.dynamicForm.form.controls.languages.value;

    return !isClientSelected || !isNameSelected || !isAnyDocAssigned || !isLanguageSelected;
  }

  confirmClientDefaultCustomization(clientId, currentCustomizationId) {
    const confirmed = this.adaCustomizationsService
      .findLanguageDefaultCustomization(clientId)
      .then((defaultCustomization: any) => {
        if (defaultCustomization && defaultCustomization.id !== currentCustomizationId) {
          return this.popupService.confirm({
            title: this.translate.instant('customizations.structures.confirmClientDefault', {
              oldDefault: defaultCustomization.name
            }),
            text: this.translate.instant('customizations.structures.confirmClientDefaultMessage')
          });
        } else {
          return Promise.resolve(true);
        }
      });
    return confirmed;
  }

  confirmLanguageDefaultCustomization(lang, currentCustomizationId) {
    const confirmed = this.adaCustomizationsService
      .findLanguageDefaultCustomization(lang)
      .then((defaultCustomization: any) => {
        if (defaultCustomization && defaultCustomization.id !== currentCustomizationId) {
          return this.popupService.confirm({
            title: this.translate.instant('customizations.structures.confirmLanguageDefault', {
              oldDefault: defaultCustomization.defaultForLanguage
            }),
            text: this.translate.instant('customizations.structures.confirmLanguageDefaultMessage')
          });
        } else {
          return Promise.resolve(true);
        }
      });
    return confirmed;
  }

  onCreate(customizationData: Object) {
    const newCustomization: AdaCustomization = Object.assign({}, customizationData, this.defaults);

    let enabledAction: Promise<any> = Promise.resolve(true);
    if (newCustomization.defaultForClient) {
      enabledAction = this.confirmClientDefaultCustomization(newCustomization.clientId, null);
    }
    if (newCustomization.defaultForLanguage) {
      enabledAction = enabledAction.then(() => {
        return this.confirmLanguageDefaultCustomization(newCustomization.defaultForLanguage, null);
      });
    }

    enabledAction
      .then(() => {
        return this.adaCustomizationsService.create(newCustomization);
      })
      .then((customization) => {
        this.saved.emit(customization);
        this.endSubmit(null);
      })
      .catch((err) => {
        this.endSubmit(err);
      });
  }

  onUpdate(updatedData: AdaCustomization) {
    const updatedCustomization: AdaCustomization = Object.assign({}, this.customization, updatedData, this.defaults);

    let enabledAction: Promise<any> = Promise.resolve(true);
    if (updatedCustomization.defaultForClient) {
      enabledAction = this.confirmClientDefaultCustomization(updatedCustomization.clientId, updatedCustomization.id);
    }
    if (updatedCustomization.defaultForLanguage) {
      enabledAction = enabledAction.then(() => {
        return this.confirmLanguageDefaultCustomization(
          updatedCustomization.defaultForLanguage,
          updatedCustomization.id
        );
      });
    }

    enabledAction
      .then(() => {
        return this.adaCustomizationsService.update(updatedCustomization);
      })
      .then((customization) => {
        this.saved.emit(customization);
        this.endSubmit(null);
      })
      .catch((err) => {
        this.endSubmit(err);
      });
  }

  endSubmit(value) {
    this.dynamicForm.submitted(value);
    if (this.openPopupFlag) {
      this.showPopup();
    }
  }

  openVisualizerPopup() {
    this.openPopupFlag = true;
    if (this.dirtyFlag || !this.checkCustomizationDataChanges()) {
      this.submit();
      this.dirtyFlag = true; // if dirty once -> always dirty until refresh or goBack
    } else {
      this.showPopup();
    }
  }

  showPopup() {
    this.visualizerPopup.popupOpen();
    this.openPopupFlag = false;
  }

  checkCustomizationDataChanges() {
    const fields = ['clientId', 'defaultForClient', 'defaultForLanguage', 'name'];
    const customizationData = this.getCustomizationFromForm();

    for (const field of fields) {
      if (customizationData[field] !== this.customization[field]) {
        return false;
      }
    }
    return this.checkPackagesChanges(customizationData);
  }

  checkPackagesChanges(customizationData) {
    if (customizationData.content.length !== this.customization.content.length) {
      return false;
    }
    for (const i in customizationData.content) {
      if (customizationData.content[i]) {
        const formContent = customizationData.content[i];
        const originalContent = this.customization.content[i];

        if (
          formContent.turn !== originalContent.turn ||
          formContent.documents.length !== originalContent.documents.length
        ) {
          return false;
        } else {
          for (const j in formContent.documents) {
            if (formContent.documents[j] !== originalContent.documents[j]) {
              return false;
            }
          }
        }
      } else {
        return false;
      }
    }
    return true;
  }

  /**
   * Drop to transfer or move the list of files
   * @param event
   * @param targetList
   */
  public onDrop(event: CdkDragDrop<string[]>, targetList?: any[]) {
    const newTargetList = targetList || event.container.data;
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, newTargetList, event.previousIndex, event.currentIndex);
    }
  }
}
